/**
 * "Two years after Hassan died" — entering a date the family stated relatively.
 *
 * This is how the evidence usually arrives. Nobody remembers 1947; they remember
 * that it was two years after a brother died, or the same month as a cousin was
 * born, or just after a wedding. Asked for a year, the honest answer is often "I
 * don't know" — and the date is lost, when the family in fact knew it well
 * enough to place.
 *
 * The widget resolves the statement into an ordinary fuzzy date and keeps the
 * wording verbatim beside it (ADR-0008). It says plainly that the link is not
 * live: if the anchor's own date is corrected later, this one does not follow.
 * Promising otherwise would be the easy lie here.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  RELATIVE_DATE_RELATIONS,
  deriveRelativeDate,
  describeRelativeDate,
  formatFuzzyDate,
  type FuzzyDateParts,
  type PersonSummary,
  type RelativeDateRelation,
} from '@famtree/shared';
import { personsApi } from '../api/client.js';
import { PersonPicker } from '../features/people/PersonPicker.js';
import { personName } from '../features/people/labels.js';
import ui from './components.module.css';

/** Which of an anchor person's dates the statement refers to. */
type AnchorEvent = 'birth' | 'death';

export interface RelativeDateFieldProps {
  legend: string;
  onResolved: (date: FuzzyDateParts | null) => void;
}

export function RelativeDateField({ legend, onResolved }: RelativeDateFieldProps): JSX.Element {
  const { t } = useTranslation();
  const [anchorPerson, setAnchorPerson] = useState<PersonSummary | null>(null);
  const [event, setEvent] = useState<AnchorEvent>('death');
  const [relation, setRelation] = useState<RelativeDateRelation>('years_after');
  const [offsetYears, setOffsetYears] = useState(2);

  // The picker gives a summary, which already carries both dates — no extra
  // fetch unless the person arrived some other way.
  const detail = useQuery({
    queryKey: ['person', anchorPerson?.id ?? ''],
    queryFn: () => personsApi.get(anchorPerson!.id),
    enabled: anchorPerson != null && anchorPerson.birth == null && anchorPerson.death == null,
  });

  const anchorDate =
    (event === 'birth' ? anchorPerson?.birth : anchorPerson?.death) ??
    (event === 'birth' ? detail.data?.person.birth : detail.data?.person.death) ??
    null;

  const anchorDescription = anchorPerson
    ? t(`relativeDate.anchorEvent.${event}`, { name: personName(anchorPerson) })
    : '';

  const wording = anchorPerson
    ? describeRelativeDate(relation, anchorDescription, offsetYears)
    : undefined;

  const resolved = anchorPerson
    ? deriveRelativeDate({
        relation,
        anchor: anchorDate,
        offsetYears,
        ...(wording ? { wording } : {}),
      })
    : null;

  const needsOffset = relation === 'years_after' || relation === 'years_before';

  return (
    <fieldset className={ui.field}>
      <legend className={ui.label}>{legend}</legend>
      <p className={ui.muted}>{t('relativeDate.help')}</p>

      <PersonPicker
        label={t('relativeDate.anchorPerson')}
        value={anchorPerson}
        onChange={setAnchorPerson}
      />

      {anchorPerson && (
        <>
          <label className={ui.field}>
            <span className={ui.label}>{t('relativeDate.event')}</span>
            <select
              className={ui.input}
              value={event}
              onChange={(e) => setEvent(e.target.value as AnchorEvent)}
            >
              <option value="birth">{t('relativeDate.events.birth')}</option>
              <option value="death">{t('relativeDate.events.death')}</option>
            </select>
          </label>

          <label className={ui.field}>
            <span className={ui.label}>{t('relativeDate.relation')}</span>
            <select
              className={ui.input}
              value={relation}
              onChange={(e) => setRelation(e.target.value as RelativeDateRelation)}
            >
              {RELATIVE_DATE_RELATIONS.map((name) => (
                <option key={name} value={name}>
                  {t(`relativeDate.relations.${name}`)}
                </option>
              ))}
            </select>
          </label>

          {needsOffset && (
            <label className={ui.field}>
              <span className={ui.label}>{t('relativeDate.years')}</span>
              <input
                className={ui.input}
                type="number"
                min={0}
                max={150}
                value={offsetYears}
                onChange={(e) => setOffsetYears(Number(e.target.value))}
              />
            </label>
          )}

          <p>
            {/* Both halves, always: what will be stored as a date, and the
                sentence it came from. */}
            <strong>{t('relativeDate.reads', { wording: wording ?? '' })}</strong>
            <br />
            {resolved?.year != null ? (
              <span className={ui.muted}>
                {t('relativeDate.stores', { date: formatFuzzyDate(resolved) ?? '' })}
              </span>
            ) : (
              <span className={ui.muted}>{t('relativeDate.storesWordingOnly')}</span>
            )}
          </p>
          <p className={ui.muted}>{t('relativeDate.notLive')}</p>

          <button
            type="button"
            className={ui.buttonSecondary}
            disabled={resolved === null}
            onClick={() => onResolved(resolved)}
          >
            {t('relativeDate.apply')}
          </button>
        </>
      )}
    </fieldset>
  );
}
